import { useCallback, useEffect, useRef } from 'preact/hooks';

import WebApp from '@twa-dev/sdk';
import cn from 'clsx';

import { useYandexInstance } from '@/app/hooks/ads/use-yandex-instance';
import { type RenderOptions } from '@/app/types/yandex';

import { AnalyticsService } from '@/shared/service/analytics/analytics-service';
import { pdAdBannerShownEvent } from '@/shared/service/analytics/events/pd-ad-banner-shown-event';
import {
  useAppState,
  useGame,
  useUserProfile,
} from '@/shared/stores/app-store/app-store';

import s from './YandexBanner.module.scss';

export type YandexProduct = 'direct' | 'rtb';

export interface YandexAdvItem {
  adId: string;
  eventHash?: string;
  hitLogId?: string;
  video?: boolean;
  bidReqIdStr?: string;
}

export interface YandexOnRenderData {
  lang?: string;
  product?: YandexProduct;
  productType?: YandexProduct | string;
  advData?: YandexAdvItem[];
}

const blockId = import.meta.env.VITE_YANDEX_ADS_BANNER_BLOCK_ID;

const YandexAdBanner = ({ className }: { className?: string }) => {
  const user = useUserProfile();
  const game = useGame();
  const { sessionId } = useAppState();

  const getInstance = useYandexInstance();
  const sentIdsRef = useRef<Set<string>>(new Set());

  const sendLogEvent = useCallback(
    (ad: YandexAdvItem) => {
      const e = pdAdBannerShownEvent({
        event_properties: {
          event_source: 'wrapper',
          source: 'banner',
          advertizer: 'yandex',
          id: ad.adId,
          game_key: game?.key,
          game_session_id: sessionId,
        },
      });
      AnalyticsService.send(e);
    },
    [game?.key, sessionId]
  );

  useEffect(() => {
    const initAd = async () => {
      try {
        const yandexInstance = await getInstance();

        if (!user?.telegramId) {
          throw new Error('Telegram ID must be passed');
        }

        if (yandexInstance) {
          yandexInstance.cb.push(() => {
            yandexInstance.yandex?.Context?.AdvManager?.render({
              type: 'banner',
              blockId,
              renderTo: `yandex_rtb_${blockId}`,
              darkTheme: WebApp.colorScheme === 'dark',
              tgUid: user.telegramId,
              statId: game?.id,
              onError: (error) => console.error('Ad error:', error),

              onRender: (data: YandexOnRenderData) => {
                if (Array.isArray(data?.advData) && !!data.advData.length) {
                  data.advData.forEach((ad: YandexAdvItem) => {
                    const key = `${blockId}:${ad.adId}`;
                    if (ad.adId && !sentIdsRef.current.has(key)) {
                      sentIdsRef.current.add(key);
                      sendLogEvent(ad);
                    }
                  });
                }
              },
            } as RenderOptions);
          });
        }
      } catch (error) {
        console.error('Failed to load Yandex ad:', error);
      }
    };

    initAd();
  }, [getInstance, game?.id, user?.telegramId, sendLogEvent]);

  return (
    <div>
      <div className={cn(className, s.container)}>
        <div id={`yandex_rtb_${blockId}`} />
      </div>
    </div>
  );
};

export default YandexAdBanner;
